<script setup lang="ts">
/**
 * Email-change flow inside the account panel. Three internal phases:
 *   form  →  request /account/email/request
 *   otp   →  request /account/email/confirm  →  done
 *   done  →  back to main
 *
 * The OTP is delivered to the *new* address (anti-takeover): the
 * lede on the OTP step says "Codice inviato al nuovo indirizzo
 * {masked_email}", masking handed back from the API.
 *
 * Mirrors the design bundle's `AccountPanel.jsx::PanelEmail`.
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { confirmEmailChange, requestEmailChange } from '@/api/portal'
import type { PortalSelfResponse } from '@/stores/portalSelf.store'
import CdsOtpInput from '../atoms/CdsOtpInput.vue'

defineProps<{
  partner: PortalSelfResponse
}>()

const emit = defineEmits<{
  back: []
  done: [partner: PortalSelfResponse]
}>()

const { t } = useI18n({ useScope: 'global' })

type Phase = 'form' | 'otp' | 'done'

const phase = ref<Phase>('form')
const newEmail = ref('')
const otpId = ref<string | null>(null)
const otp = ref('')
const maskedEmail = ref('')
const error = ref('')
const submitting = ref(false)
const demoCode = ref<string | null>(null)
const isDev = import.meta.env.DEV

const submitDisabled = computed(() => submitting.value || newEmail.value.trim().length === 0)

async function onRequest(e?: Event): Promise<void> {
  e?.preventDefault()
  if (submitDisabled.value) return
  error.value = ''
  submitting.value = true
  try {
    const r = await requestEmailChange({ new_email: newEmail.value.trim() })
    otpId.value = r.data.otp_id
    maskedEmail.value = r.data.masked_email
    otp.value = ''
    phase.value = 'otp'
    demoCode.value = isDev ? (r.data.demo_code ?? null) : null
  } catch (err) {
    const status = readStatus(err)
    if (status === 400) error.value = t('portal.account_panel.errors.email.invalid')
    else if (status === 409) error.value = t('portal.account_panel.errors.email.already_yours')
    else error.value = t('portal.account_panel.errors.email.generic')
  } finally {
    submitting.value = false
  }
}

async function onConfirm(e?: Event): Promise<void> {
  e?.preventDefault()
  if (submitting.value || otp.value.length !== 6 || !otpId.value) return
  error.value = ''
  submitting.value = true
  try {
    const r = await confirmEmailChange({ otp_id: otpId.value, code: otp.value })
    phase.value = 'done'
    emit('done', r.data.partner)
  } catch (err) {
    const status = readStatus(err)
    if (status === 401) error.value = t('portal.account_panel.errors.email.otp_invalid')
    else if (status === 410) error.value = t('portal.account_panel.errors.email.otp_expired')
    else if (status === 429) error.value = t('portal.account_panel.errors.email.too_many')
    else error.value = t('portal.account_panel.errors.email.generic')
    otp.value = ''
  } finally {
    submitting.value = false
  }
}

function backToForm(): void {
  phase.value = 'form'
  error.value = ''
  otp.value = ''
}

function readStatus(err: unknown): number | null {
  if (err && typeof err === 'object' && 'response' in err) {
    const r = (err as { response?: { status?: number } }).response
    return r?.status ?? null
  }
  return null
}
</script>

<template>
  <!-- Done -->
  <div v-if="phase === 'done'" class="pp-panel__done">
    <div class="pp-panel__done-icon">
      <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        aria-hidden="true"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
    <h3>{{ t('portal.account_panel.email.done.title') }}</h3>
    <p>{{ t('portal.account_panel.email.done.body', { email: newEmail }) }}</p>
    <button type="button" class="btn btn--primary" @click="emit('back')">
      <span>{{ t('portal.account_panel.email.done.return') }}</span>
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        aria-hidden="true"
      >
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
      </svg>
    </button>
  </div>

  <!-- OTP -->
  <form v-else-if="phase === 'otp'" novalidate @submit="onConfirm">
    <p class="pp-panel__lede">
      {{ t('portal.account_panel.email.otp_lede', { email: maskedEmail }) }}
    </p>
    <CdsOtpInput v-model="otp" :disabled="submitting" :error="!!error" />
    <div v-if="error" class="pp-auth__error pp-account-email__error">
      {{ error }}
    </div>
    <div v-if="demoCode" class="pp-auth__demo-hint pp-account-email__demo">
      <span>{{ t('portal.account_panel.email.otp_demo_label') }}</span>
      <strong class="mono">{{ demoCode }}</strong>
    </div>
    <div class="pp-panel__foot">
      <button type="button" class="btn btn--ghost" @click="backToForm">
        {{ t('portal.account_panel.email.otp_back') }}
      </button>
      <button type="submit" class="btn btn--primary" :disabled="submitting || otp.length !== 6">
        <span>{{
          submitting
            ? t('portal.account_panel.email.otp_confirming')
            : t('portal.account_panel.email.otp_confirm')
        }}</span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </button>
    </div>
  </form>

  <!-- Form -->
  <form v-else novalidate @submit="onRequest">
    <div class="pp-panel__current">
      <div class="pp-panel__current-k">
        {{ t('portal.account_panel.email.current_label') }}
      </div>
      <div class="pp-panel__current-v mono">{{ partner.email }}</div>
    </div>
    <label class="pp-field">
      <span class="pp-field__label">
        {{ t('portal.account_panel.email.new_label') }}
      </span>
      <input
        v-model="newEmail"
        type="email"
        class="pp-field__input"
        :placeholder="t('portal.account_panel.email.new_placeholder')"
        autofocus
      />
    </label>
    <div v-if="error" class="pp-auth__error">{{ error }}</div>
    <div class="pp-panel__hint pp-account-email__hint-spaced">
      {{ t('portal.account_panel.email.otp_hint') }}
    </div>
    <div class="pp-panel__foot">
      <button type="button" class="btn btn--ghost" @click="emit('back')">
        {{ t('portal.account_panel.email.otp_back') }}
      </button>
      <button type="submit" class="btn btn--primary" :disabled="submitDisabled">
        <span>{{
          submitting
            ? t('portal.account_panel.email.submitting')
            : t('portal.account_panel.email.submit')
        }}</span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </button>
    </div>
  </form>
</template>

<style scoped>
.pp-account-email__error,
.pp-account-email__demo {
  margin-top: var(--spacing-04);
}
.pp-account-email__hint-spaced {
  margin-top: var(--spacing-04);
}
</style>
